import organizationClient from "@apis/organizationClient";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { hasFeature } from "@/hooks/useFeatures";
import { UserPermission } from "@/types/generated/UserPermission";
import { hasPermission } from "@/hooks/usePermissions";
import type { RouteCondition } from "@/types/RouteConfig";

/**
 * STIC-15133: access condition for /quotes_storefront_partner,
 * /quotes_partner, /quote_detail_partner — the partner quotation tool.
 *
 * The quotation tool is only available to commercial partners on the EU
 * price list (the EU limitation is temporary — US rollout still needs
 * organizational alignment). Mirrors the sidebar menu visibility rule and
 * the backend's @partnerQuoteAccessPermission:
 * - quote_manage feature + MENU_ADMIN_QUOTES permission
 * - partner users must belong to a commercial partner on the EU price list
 * - when browsing a PARTNER org (?org=...), that org must be a commercial
 *   partner on the EU price list
 */
export const PARTNER_QUOTES_ACCESS: RouteCondition = async ({
  loginInfo,
  orgId,
}) => {
  if (
    !hasFeature(FeatureKey.quote_manage) ||
    !hasPermission(UserPermission.MENU_ADMIN_QUOTES)
  ) {
    return false;
  }

  const isPartnerUser = loginInfo.organisation?.organisationType === "PARTNER";
  if (
    isPartnerUser &&
    !(
      loginInfo.partner?.commercialPartner &&
      loginInfo.organisation?.partnerPriceList === "EU"
    )
  ) {
    return false;
  }

  // Browsing another org via ?org= — mirror useSelectedOrgMenu: only fetch
  // when it isn't the user's own org and the user may view organisations.
  const needsOrgCheck =
    !!orgId &&
    orgId !== loginInfo.organisation?.id &&
    hasPermission(UserPermission.ORGANIZATION_VIEW);
  if (!needsOrgCheck) {
    return true;
  }

  const { data } = await organizationClient.findOne({ id: orgId });
  if (data?.organisationType !== "PARTNER") {
    // Customer orgs: the viewer-level checks above are what matters
    return true;
  }
  return (
    (!!data.partner?.commercialPartner ||
      !!loginInfo.partner?.commercialPartner) &&
    (data.partnerPriceList === "EU" ||
      loginInfo.organisation?.partnerPriceList === "EU")
  );
};

/**
 * STIC-15133: access condition for /quotes_storefront, /quotes,
 * /quote_detail — internal sales tooling. Partner users must use the partner
 * variants (which carry the commercial + EU rule): now that quote_manage is
 * part of the partner plan, the feature alone no longer implies access here.
 */
export const INTERNAL_QUOTES_ACCESS: RouteCondition = ({ loginInfo }) =>
  loginInfo.organisation?.organisationType !== "PARTNER";
